import React, { useState } from 'react'

import { Check, ErrorOutline } from '@mui/icons-material'
import { Dialog, Tooltip, Grid, Button } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classnames from 'classnames'
import { useDispatch } from 'react-redux'

import { worklogsForbiddenToBeSubmittedSelector } from '../../actions/submittableWorkLogsSelector'
import {
  FailedSubmissionResponse,
  submitWorklogs,
  SubmitWorklogsResponse,
  switchMode,
} from '../../actions/trackerActions'
import Loading from '../../components/Loading'
import { Mode } from '../../consts'
import { useAreSubmittedWorklogsInJira } from '../../hooks/useAreSubmittedWorklogsInJira'
import { useSelector } from '../../hooks/useSelector'
import { FtAndJiraInSync } from '../FtAndJiraInSync'
import * as styles from '../WorkLogs/styles'

import LoggedTimeSummary from './LoggedTimeSummary'

const useStyles = makeStyles((theme) => ({
  worklogs: {
    ...styles.worklogs,
    width: '100%',
    height: '100%',
    maxWidth: 678,
    margin: `${theme.spacing(3)} auto ${theme.spacing(6)}`,
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.palette.boxShadow.review,
    borderRadius: 4,
  },
  btnGroup: {
    position: 'absolute',
    bottom: 0,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.border.main}`,
  },
  btnWrapper: {
    padding: theme.spacing(1),
  },
  btn: {
    height: '32px',
    lineHeight: '31px',
    textTransform: 'none',
    borderRadius: '2px',
    padding: theme.spacing(0, 3),
  },
}))

// TODO: move to its own file
type ShowStatusDialogStatus = {
  show: boolean
  loading: boolean
  success: boolean
  noOfSynced: number
  failedWorklogs: FailedSubmissionResponse[]
}
type ShowStatusDialogProps = {
  status: ShowStatusDialogStatus
}

const ShowStatusDialog = ({
  status: { loading, noOfSynced, success, failedWorklogs },
}: ShowStatusDialogProps) => {
  const statusMap = {
    loading: {
      icon: <Loading />,
      message: '',
    },
    success: {
      icon: <Check color="primary" />,
      message: `Successfully synced ${noOfSynced} worklogs`,
    },
    failure: {
      icon: <ErrorOutline color="primary" />,
      message: `Error: ${failedWorklogs.length} of ${
        noOfSynced + failedWorklogs.length
      } worklogs failed to sync`,
    },
  }

  const status = loading ? 'loading' : success ? 'success' : 'failure'
  const { icon, message } = statusMap[status]

  return (
    <Dialog open>
      <span>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {icon}
          {!!message && (
            <span style={{ marginLeft: '5px', fontWeight: 'bold' }}>
              {message}
            </span>
          )}
        </div>

        {!!failedWorklogs.length && (
          <div style={{ margin: '5px' }}>
            <span>The following worklogs couldn't be synced:</span>
            <ul>
              {failedWorklogs.map(({ worklog }) => (
                <li>
                  <span>{worklog.issue}</span>: <span>{worklog.comment}</span>
                </li>
              ))}
            </ul>
            <span>
              If the problem persists, make sure the issues are <br />
              not <i>closed</i> or contact the #fine-tracker Slack channel.
            </span>
          </div>
        )}
      </span>
    </Dialog>
  )
}

export const WorklogsToSave = () => {
  const classes = useStyles()
  //TODO: TS-FIX
  const dispatch: any = useDispatch()

  const issues = useSelector((state) => state.issues)
  const forbiddenWorklogs = useSelector(worklogsForbiddenToBeSubmittedSelector)
  const tutorialEnabled = useSelector((state) => state.tutorial.enabled)
  const defaultStatus: ShowStatusDialogStatus = {
    show: false,
    loading: false,
    success: true,
    noOfSynced: 0,
    failedWorklogs: [],
  }
  const [status, setStatus] = useState(defaultStatus)

  const {
    allWorklogsInJira,
    worklogsNotInJira,
    worklogsDiffFromJira,
    worklogsInJiraLoading,
  } = useAreSubmittedWorklogsInJira()

  const handleSubmit = async () => {
    // if you hold enter in tutorial you are able to submit the worklogs
    if (tutorialEnabled) return

    setStatus({ ...status, show: true, loading: true })
    const submitStatus: SubmitWorklogsResponse = await dispatch(
      submitWorklogs()
    )
    setStatus({
      show: true,
      loading: false,
      success: submitStatus.success,
      noOfSynced: submitStatus.noOfSynced,
      failedWorklogs: submitStatus.failedWorklogs,
    })
    setTimeout(
      () =>
        !submitStatus.success
          ? setStatus(defaultStatus)
          : dispatch(switchMode(Mode.LOGGING)),
      submitStatus.success ? 2000 : 5000
    )
  }

  const submitDisabled = forbiddenWorklogs.length > 0 || worklogsInJiraLoading

  return (
    <div className={classnames(classes.worklogs, 'tutorial-worklogsToSubmit')}>
      {issues.length < 1 ? (
        <Loading />
      ) : (
        <>
          {status.show && <ShowStatusDialog status={status} />}
          <LoggedTimeSummary />
          <Grid
            container
            alignItems="center"
            justifyContent="flex-end"
            className={classes.btnGroup}
          >
            <FtAndJiraInSync
              allWorklogsInJira={allWorklogsInJira}
              worklogsNotInJira={worklogsNotInJira}
              worklogsDiffFromJira={worklogsDiffFromJira}
              worklogsInJiraLoading={worklogsInJiraLoading}
            />
            <div className={classes.btnWrapper}>
              <Button
                variant="outlined"
                color="primary"
                className={classes.btn}
                data-cy="save-back-button"
                onClick={() => dispatch(switchMode(Mode.LOGGING))}
              >
                Back
              </Button>
            </div>
            <div
              className={classnames(
                classes.btnWrapper,
                'tutorial-worklogsToSave'
              )}
            >
              <Tooltip
                title={
                  forbiddenWorklogs.length > 0
                    ? "You can't submit worklogs into an already closed month(s). Please mark them as free time before submitting."
                    : ''
                }
                placement="top"
              >
                <span>
                  <Button
                    disabled={submitDisabled}
                    autoFocus
                    variant="contained"
                    color={allWorklogsInJira ? 'primary' : 'error'}
                    disableElevation
                    aria-label="Submit worklogs"
                    data-cy="save-jira-submit"
                    className={classes.btn}
                    onClick={handleSubmit}
                  >
                    {allWorklogsInJira ? 'Save to JIRA' : 'Force save to JIRA'}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </Grid>
        </>
      )}
    </div>
  )
}
